import { useEffect, useState, useCallback, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { getEnvironmentConfig } from '../environmentConfig';

export interface RunCompletedMessage {
    type: 'RunCompleted';
    payload: {
        runId: string;
        graphId: string;
        duration: number;
        hasError: boolean;
        createdAt: string;
    };
}

interface ConnectedMessage {
    type: 'Connected';
    payload: {
        connectionId: string;
        graphId: string;
    };
}

type WebSocketMessage = RunCompletedMessage | ConnectedMessage;

function useWebSocket(graphId: string): WebSocket | null {
    const { session } = useSession();
    const [webSocket, setWebSocket] = useState<WebSocket | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const socketRef = useRef<WebSocket | null>(null);

    const connect = useCallback(async () => {
        if (!session) return;

        // Clean up any existing connection
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        const socket = new WebSocket(`${getEnvironmentConfig().liveSyncServiceUrl}?graphId=${graphId}`);
        socketRef.current = socket;

        socket.addEventListener('open', async () => {
            try {
                const token = await session.getToken();
                if (token && socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify({ type: 'auth', token }));
                }
            } catch (error) {
                console.error('Failed to get auth token:', error);
                socket.close();
            }
        });

        socket.addEventListener('message', event => {
            try {
                const data: WebSocketMessage = JSON.parse(event.data);
                if (data.type === 'Connected') {
                    setIsAuthenticated(true);
                    setWebSocket(socket);
                }
            } catch (error) {
                console.error('Failed to parse WebSocket message:', error);
            }
        });

        socket.addEventListener('close', () => {
            setWebSocket(null);
            setIsAuthenticated(false);
            socketRef.current = null;
        });

        socket.addEventListener('error', () => {
            setWebSocket(null);
            setIsAuthenticated(false);
            socketRef.current = null;
        });
    }, [graphId, session]);

    useEffect(() => {
        connect();

        return () => {
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            setWebSocket(null);
            setIsAuthenticated(false);
        };
    }, [connect]);

    return isAuthenticated ? webSocket : null;
}

export const useLiveSync = (
    graphId: string,
    setExecutionRunning?: (running: boolean) => void,
    onRunCompleted?: (notification: RunCompletedMessage['payload']) => void,
) => {
    const webSocket = useWebSocket(graphId);

    useEffect(() => {
        if (!webSocket) return;

        const handleMessage = (event: MessageEvent) => {
            try {
                const data: WebSocketMessage = JSON.parse(event.data);

                if (data.type === 'RunCompleted') {
                    setExecutionRunning?.(false);
                    onRunCompleted?.(data.payload);
                }
            } catch (error) {
                console.error('Failed to handle WebSocket message:', error);
            }
        };

        webSocket.addEventListener('message', handleMessage);

        return () => {
            webSocket.removeEventListener('message', handleMessage);
        };
    }, [setExecutionRunning, onRunCompleted, webSocket]);
};
